import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  FileText,
  Ticket,
  Calendar,
  Clock,
  Bug,
  Bell,
  MessageSquare,
  X,
  Minus
} from 'lucide-react';
import { Badge } from '../ui/badge';
import api from '../../lib/axios';

// =============================================
// CLIENT FLOATING ACTION BUTTON (FAB) WIDGET
// Menu d'actions rapides spécifique pour les clients
// =============================================

const ClientFloatingWidget = () => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [pendingActions, setPendingActions] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const menuRef = useRef(null);

  // Fetch pending counts
  const fetchCounts = useCallback(async () => {
    if (!user || !isClient) return;
    
    try {
      // Fetch pending project requests (waiting for client action)
      const requestsRes = await api.get('/project-requests/');
      const requests = requestsRes.data || [];
      const pendingEstimates = requests.filter(r => 
        r.status === 'estimated' || r.status === 'quote_sent'
      ).length;
      setPendingRequests(pendingEstimates);
    } catch (e) { /* ignore */ }
    
    try {
      // Fetch pending actions (contracts to sign, invoices to pay, etc.)
      const contractsRes = await api.get('/contracts/pending-signatures');
      const contracts = contractsRes.data || [];
      setPendingActions(contracts.length);
    } catch (e) { /* ignore */ }
  }, [user, isClient]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Only render for clients
  if (!user || !isClient) return null;

  const accentColor = theme?.colors?.accent || '#00d4aa';
  
  // Check if we're on a chat page
  const isOnChatPage = location.pathname.includes('/chat') || 
                       location.pathname.includes('/project/') && location.pathname.includes('/chat');

  // Menu items configuration for clients
  const menuItems = [
    {
      id: 'chat',
      icon: MessageSquare,
      label: 'Chat Agence',
      color: '#06B6D4', // Cyan
      onClick: () => navigate('/chat')
    },
    {
      id: 'new-request',
      icon: FileText,
      label: 'Nouvelle demande',
      color: '#10B981', // Emerald green
      onClick: () => navigate('/new-project-request')
    },
    {
      id: 'new-ticket',
      icon: Ticket,
      label: 'Nouveau ticket',
      color: '#F59E0B', // Amber
      onClick: () => navigate('/tickets?action=new')
    },
    {
      id: 'appointment',
      icon: Calendar,
      label: 'Demande de RDV',
      color: '#0EA5E9', // Sky blue
      onClick: () => navigate('/my-appointments')
    },
    {
      id: 'pending-actions',
      icon: Clock,
      label: 'Actions en attente',
      color: '#8B5CF6', // Purple
      badge: pendingRequests + pendingActions,
      onClick: () => navigate('/my-requests')
    },
    {
      id: 'bug-report',
      icon: Bug,
      label: 'Remontée de bug',
      color: '#EF4444', // Red
      onClick: () => {
        // Trigger the bug report dialog
        const bugButton = document.querySelector('[data-testid="bug-report-button"]');
        if (bugButton) {
          bugButton.click();
        }
      }
    }
  ];

  const totalBadge = pendingRequests + pendingActions;

  // On chat pages, position higher to avoid input field overlap
  const bottomPosition = isOnChatPage ? '100px' : '24px';

  return (
    <div 
      ref={menuRef}
      data-testid="client-floating-widget"
      style={{
        position: 'fixed',
        bottom: bottomPosition,
        right: '16px',
        zIndex: 9997,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '12px',
        transition: 'bottom 0.3s ease',
        // CRITICAL: Allow clicks to pass through the container to elements below
        pointerEvents: 'none'
      }}
    >
      {/* Menu Items - Appear when open */}
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '12px',
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
      >
        {menuItems.map((item, index) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'translateX(0)' : 'translateX(20px)',
              transition: `all 0.3s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s`
            }}
          >
            {/* Label */}
            <span
              style={{
                backgroundColor: 'white',
                color: '#374151',
                padding: '8px 16px',
                borderRadius: '24px',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {item.label}
              {item.badge > 0 && (
                <span
                  style={{
                    backgroundColor: '#EF4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}
                >
                  {item.badge}
                </span>
              )}
            </span>
            
            {/* Icon Button */}
            <button
              data-testid={`client-fab-${item.id}`}
              onClick={() => {
                setIsOpen(false);
                item.onClick();
              }}
              className="w-10 h-10 md:w-12 md:h-12"
              style={{
                borderRadius: '50%',
                backgroundColor: item.color,
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
              }}
            >
              <item.icon size={20} />
              
              {/* Badge on icon */}
              {item.badge > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    minWidth: '22px',
                    height: '22px',
                    borderRadius: '11px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 6px',
                    border: '2px solid white'
                  }}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Main FAB Button - Smaller on mobile */}
      <button
        data-testid="client-fab-main-button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 md:w-14 md:h-14"
        style={{
          borderRadius: '50%',
          backgroundColor: isOpen ? '#EF4444' : accentColor,
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 15px rgba(0,0,0,0.25)',
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          // Re-enable pointer events for the button itself
          pointerEvents: 'auto'
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.transform = 'scale(1.1)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <div
          style={{
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Plus size={24} strokeWidth={2.5} />
        </div>
        
        {/* Total Badge (when closed) */}
        {!isOpen && totalBadge > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '24px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: '#EF4444',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              border: '2px solid white',
              animation: 'pulse 2s infinite'
            }}
          >
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </button>

      {/* CSS Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        /* Mobile styles */
        @media (max-width: 768px) {
          [data-testid="client-floating-widget"] {
            bottom: 16px !important;
            right: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientFloatingWidget;
